import type { MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import {
  deferTracking,
  sendGAEvent,
  sendGALinkClickEvent,
} from '@noths/polaris-client-google-analytics';
import type {
  LinkCarouselItemClickPayload,
  LinkCarouselProps,
} from '@noths/polaris-client-ribbons-design-system';

import { RELATED_CATEGORIES_TITLE } from 'src/components/organisms/LinkCarousel/constants/copy';
import { selectRelatedCategories } from 'src/components/organisms/LinkCarousel/modules/selectors';

/**
 * View model for the related-categories link carousel. Owns the categories read
 * and the impression/click tracking, so the presentational carousel stays pure
 * props.
 *
 * Returns null when the product has no related categories, which is how the
 * section is omitted from the page.
 */
export type LinkCarouselViewModel = Required<
  Pick<LinkCarouselProps, 'items' | 'onItemClick' | 'onVisible' | 'title'>
>;

export const useLinkCarouselViewModel = (): LinkCarouselViewModel | null => {
  const items = useSelector(selectRelatedCategories);

  if (items.length === 0) {
    return null;
  }

  return {
    items,
    onItemClick: (
      evt: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
      { index }: LinkCarouselItemClickPayload,
    ) =>
      sendGALinkClickEvent(evt, {
        event: 'custom_event',
        event_category: 'Related Category',
        event_action: 'Link Click',
        event_label: items[index].text,
      }),
    onVisible: () =>
      deferTracking(() =>
        sendGAEvent({
          event: 'custom_event',
          event_category: 'Related Category',
          event_action: 'Impression',
          event_label: 'not set',
        }),
      ),
    title: RELATED_CATEGORIES_TITLE,
  };
};
